import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import KpiGrid from '../components/KpiGrid'
import DataTable, { type Column } from '../components/DataTable'
import StatusPill from '../components/StatusPill'
import WindowChip from '../components/WindowChip'
import { BANDEJA_CAMBIO } from '../eventos'
import { QUE_ES_UN_CLIENTE_ESPERANDO } from '../sla'
import type { PerformanceResponse, PerformanceRow } from '../types/portal'

/**
 * "Mi desempeño" — los indicadores propios del asesor y sus atenciones del día.
 *
 * Todo se calcula en el servidor (`GET /me/performance`,
 * `operacion/desempeno.py`) sobre lo que quien pregunta tiene asignado y lo
 * que resolvió: un asesor no ve el desempeño de otro. Las métricas del equipo
 * son la pantalla del supervisor.
 *
 * Se actualiza sola (03-nov-2026; antes pedía una vez al abrirse): cuando el
 * Layout ve que cambió lo asignado (`BANDEJA_CAMBIO`, lo mismo que hace sonar
 * la campana), al volver a la pestaña y cada minuto, por lo que el contador
 * no ve. Sin pedidos superpuestos, y un sondeo fallido no borra lo que ya se
 * veía: lo dice en el subtítulo.
 */

/** Cada cuánto se vuelve a pedir con la pantalla abierta. */
const REFRESCO_MS = 60_000

const OUTCOME_LABEL: Record<string, string> = {
  interesado: 'Interesado',
  no_contesta: 'No contesta',
  no_califica: 'No califica',
  otro: 'Otro',
}

function hora(ts: string | null | undefined): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleTimeString('es-PE', {
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Lima',
  })
}

function resultado(r: PerformanceRow) {
  if (r.cierre_automatico) {
    return (
      <span className="badge-gray" title="Venció la sesión del socio; nadie la marcó resuelta">
        Se cerró sola
      </span>
    )
  }
  if (!r.resolved_at) return <span className="text-ink-faint">—</span>
  return r.outcome
    ? <span className="badge-gray">{OUTCOME_LABEL[r.outcome] ?? r.outcome}</span>
    : <span className="text-ink-faint">Sin tipificar</span>
}

export default function MyPerformance() {
  const [data, setData] = useState<PerformanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [leidoA, setLeidoA] = useState<string | null>(null)
  const enCamino = useRef(false)

  const pedir = useCallback(async () => {
    if (enCamino.current) return
    enCamino.current = true
    try {
      const res = await apiClient.get<PerformanceResponse>('/me/performance')
      setData(res.data)
      setError(null)
      setLeidoA(new Date().toISOString())
    } catch (err) {
      setError(errorMessage(err, 'No se pudo cargar tu desempeño.'))
    } finally {
      enCamino.current = false
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void pedir()
    const refrescar = () => { void pedir() }
    const alVolver = () => { if (document.visibilityState === 'visible') refrescar() }
    const id = setInterval(refrescar, REFRESCO_MS)
    window.addEventListener(BANDEJA_CAMBIO, refrescar)
    document.addEventListener('visibilitychange', alVolver)
    return () => {
      clearInterval(id)
      window.removeEventListener(BANDEJA_CAMBIO, refrescar)
      document.removeEventListener('visibilitychange', alVolver)
    }
  }, [pedir])

  const k = data?.kpis
  const columnas: Column<PerformanceRow>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      cell: (r) => <span className="font-semibold">{r.contact_name || 'Sin nombre'}</span>,
      sub: (r) => r.phone,
    },
    { key: 'estado', header: 'Estado', cell: (r) => <StatusPill status={r.status} /> },
    { key: 'ventana', header: 'Ventana 24 h', cell: (r) => <WindowChip window={r.window} compact /> },
    {
      key: 'asignada',
      header: 'Asignada',
      cell: (r) => <span className="font-mono text-micro">{hora(r.assigned_at)}</span>,
    },
    {
      key: 'cierre',
      header: 'Cerrada',
      cell: (r) => <span className="font-mono text-micro">{hora(r.resolved_at)}</span>,
    },
    { key: 'resultado', header: 'Resultado', align: 'right', cell: resultado },
  ]

  // Con datos a la vista, un sondeo fallido no los tapa: lo dice acá.
  const partes = ['Lo que te asignaron o cerraste hoy, en hora de Lima']
  if (data && error) partes.push(`no se pudo actualizar; datos de las ${hora(leidoA)}`)
  else if (leidoA) partes.push(`actualizado ${hora(leidoA)}`)
  if (data && !data.completo) {
    partes.push('lectura parcial: hay más conversaciones de las que se pudieron leer')
  }

  return (
    <div className="flex flex-col gap-5">
      <KpiGrid
        kpis={[
          { label: 'En tu bandeja', value: k?.en_bandeja ?? '—',
            hint: 'Asignadas a ti ahora, en cualquier estado' },
          { label: 'Resueltas hoy', value: k?.resueltas_hoy ?? '—',
            hint: 'Las que marcaste resueltas hoy. No cuenta las que se cerraron solas' },
          { label: 'Cliente esperando', value: k?.cliente_esperando ?? '—',
            hint: QUE_ES_UN_CLIENTE_ESPERANDO,
            tone: (k?.cliente_esperando ?? 0) > 0 ? 'danger' : 'default' },
          { label: 'Ventana por vencer', value: k?.por_vencer ?? '—',
            hint: 'Menos de 2 h, o ya cerrada, para escribirle sin plantilla',
            tone: (k?.por_vencer ?? 0) > 0 ? 'warn' : 'default' },
        ]}
      />

      <DataTable
        title="Atenciones de hoy"
        subtitle={partes.join(' · ')}
        columns={columnas}
        rows={data?.atenciones ?? []}
        rowKey={(r) => r.conversation_id}
        loading={loading && !data}
        error={data ? null : error}
        onRetry={() => void pedir()}
        emptyTitle="Todavía no atendiste nada hoy"
        emptyMessage="Lo que te asignen y lo que resuelvas hoy va a aparecer acá."
      />
    </div>
  )
}
